import React, { FC, useEffect, useState } from "react";
import {
    DropdownAction,
    Product,
    ProductMetaData,
    ProductVariables,
    Project,
    UserProduct,
    UserProductData,
    UserProductVariables,
    UserProject
} from "../../models/types";
import {
    Button,
    Card,
    Col,
    Collapse,
    OverlayTrigger,
    Row,
    Tooltip
} from "react-bootstrap";
import { useApolloClient, useMutation, useQuery } from "@apollo/client";
import {
    GET_PRODUCT_META_DATA,
    GET_PROJECTS_BY_PRODUCT,
    GET_USER_PROJECTS
} from "../../graphql/queries";
import { UPDATE_PRODUCT, UPDATE_USER_PRODUCT } from "../../graphql/mutations";
import { EditableTextComponent } from "../editable/EditableTextComponent";
import { ActionDropdown } from "../dropdowns/ActionDropdown";
import { DailyCTR } from "./analytics/DailyCTR";
import { DailyImpressions } from "./analytics/DailyImpressions";
import { Projects } from "../views/Projects";
import { useLocalAnalyticsState, useLocalState } from "../../graphql/hooks";
import { AnalyticsFunnelSelectionToggle } from "../buttons/analytics/AnalyticsFunnelSelectionToggle";
import {
    saveCollapseState,
    updateCollapseState,
    updateCollapseTiersInCache,
    updateProductFavorites
} from "../../common/Helpers";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faStar } from "@fortawesome/free-solid-svg-icons";
import { SimpleDate } from "./SimpleDate";
import { UserQueryImpressions } from "./analytics/UserQueryImpressions";
import { UserQueryCTR } from "./analytics/UserQueryCTR";
import { StatusBadges } from "./StatusBadges";
import { AlertModal } from "../modals/AlertModal";
import cx from "classnames";

interface Props {
    product: Product;
    userProduct?: UserProduct;
    onCreate?: (variables?: any) => void;
    onRemove: () => void;
}

const ProductCardItemImplementation: FC<Props> = ({
    product,
    userProduct,
    onCreate,
    onRemove
}) => {
    const client = useApolloClient();
    const state = useLocalState();
    const { analyticsEnabled, analyticsFunnelSelectionMode } =
        useLocalAnalyticsState();
    const [showChildren, updateShowChildren] = useState(
        state.productTier && state.productTier.has(Number(product.id))
    );
    const [loaded, updateLoaded] = useState(false);
    const [favoriteProjects, setFavoriteProjects] = useState<UserProject[]>([]);

    const { data: { productMetaData } = {} } = useQuery<
        ProductMetaData,
        ProductVariables
    >(GET_PRODUCT_META_DATA, {
        variables: { productId: Number(product?.id) },
        fetchPolicy: "network-only"
    });

    const { data: projectsData } = useQuery(GET_PROJECTS_BY_PRODUCT, {
        variables: { productId: Number(product?.id) }
    });

    const { data: userProjectsData } = useQuery(GET_USER_PROJECTS, {
        fetchPolicy: "cache-only",
        skip: !userProduct
    });

    const [updateUserProduct] = useMutation<
        UserProductData,
        UserProductVariables
    >(UPDATE_USER_PRODUCT);

    useEffect(() => {
        if (
            projectsData?.projects &&
            userProjectsData?.userProjects &&
            userProduct
        ) {
            const productProjects = projectsData.projects as Project[];
            const userProjects = userProjectsData.userProjects as UserProject[];

            const favoritedProjectsForProduct = userProjects.filter(
                userProject => {
                    const project = productProjects.find(
                        p => p.id === userProject.project.id
                    );
                    return project !== undefined;
                }
            );
            setFavoriteProjects(favoritedProjectsForProduct);
        }
    }, [projectsData, userProjectsData, userProduct]);

    useEffect(() => {
        const updatedSet = updateCollapseState(
            state.productTier,
            Number(product.id),
            showChildren
        );
        saveCollapseState(0, updatedSet);
        updateCollapseTiersInCache(client, updatedSet);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showChildren]);

    const actions: DropdownAction[] = [];

    if (onCreate) {
        actions.push({
            header: "New Project",
            variant: "inverse",
            callback: onCreate
        });
        actions.push({
            header: "Remove Product",
            variant: "danger",
            callback: onRemove
        });
    }

    const hasProjects = (projectsData?.projects?.length ?? 0) > 0;

    const productColumnClass = analyticsEnabled ? "col-3" : "col-5 col-xl-4";
    const statusColumnClass = analyticsEnabled ? "col-1" : "col-2 col-xl-3";

    const favoriteClass = !!userProduct ? "text-warning" : "text-inverse-25";
    const [showRemoveAlert, updateShowRemoveAlert] = useState(false);

    const handleFavoriteClick = (event: React.MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();

        if (userProduct) {
            if (favoriteProjects.length > 0) {
                updateShowRemoveAlert(true);
            } else {
                handleFavoriteUpdate();
            }
        } else {
            handleFavoriteUpdate();
        }
    };

    const handleFavoriteUpdate = async () => {
        const result = await updateUserProduct({
            variables: {
                input: {
                    productId: Number(product.id),
                    favorite: !userProduct
                }
            },
            refetchQueries: [
                {
                    query: GET_USER_PROJECTS
                }
            ]
        });
        updateProductFavorites(client, result.data?.userProduct, product.id);
    };

    return (
        <Card className="no-border inner-card">
            <Card.Header
                className="text-inverse mouseHover custom-card-product"
                onClick={e => {
                    if (e.defaultPrevented) {
                        return;
                    }
                    updateShowChildren(!showChildren);
                }}
            >
                <Row className="d-flex align-items-center mb-1">
                    <Col
                        className={`d-flex align-items-center ${productColumnClass}`}
                    >
                        <OverlayTrigger
                            key={Date.now()}
                            delay={{
                                show: 550,
                                hide: 100
                            }}
                            overlay={<Tooltip>Toggle Favorite</Tooltip>}
                        >
                            <FontAwesomeIcon
                                icon={faStar}
                                size="xs"
                                className={`me-1 ${favoriteClass}`}
                                onClick={handleFavoriteClick}
                            />
                        </OverlayTrigger>
                        <span className="ms-2 medium-font">
                            <EditableTextComponent
                                productId={product.id}
                                isEditable={true}
                                text={product.name}
                                mutation={UPDATE_PRODUCT}
                                valueName="name"
                                className={{
                                    container: "text-start"
                                }}
                            />
                        </span>
                        <div className="icon-placeholder ms-2">
                            {hasProjects ? (
                                <FontAwesomeIcon
                                    className={`icon-chevron ${
                                        showChildren ? "rotate-180" : ""
                                    }`}
                                    icon={faChevronDown}
                                />
                            ) : null}
                        </div>
                    </Col>
                    <Col
                        className={cx("d-flex", {
                            "col-2": analyticsEnabled,
                            "col-3": !analyticsEnabled
                        })}
                    >
                        <span className="product-title text-inverse-50 ps-2 medium-font">
                            {product?.organization?.name}
                        </span>
                    </Col>
                    <Col className="col-1 d-flex xs-font p-1 text-nowrap justify-content-center">
                        <SimpleDate dateUpdated={product.dateUpdated} />
                    </Col>
                    <Col
                        className={cx(
                            "ps-3 d-flex text-nowrap justify-content-center",
                            statusColumnClass
                        )}
                    >
                        {productMetaData ? (
                            <StatusBadges metaData={productMetaData} />
                        ) : null}
                    </Col>
                    {analyticsEnabled && (
                        <>
                            <Col className="col-1 ps-2 text-start daily-impr-product">
                                <DailyImpressions
                                    impressions={product.impressions}
                                />
                            </Col>
                            <Col className="col-1 text-start pe-0">
                                <DailyCTR
                                    gameplay={product.gameplay}
                                    endScreen={product.endScreen}
                                    impressions={product.impressions}
                                />
                            </Col>
                            <Col className="col-1 text-start pe-0">
                                <UserQueryImpressions
                                    impressions={product.userImpressions}
                                />
                            </Col>
                            <Col className="col-1 text-start pe-0">
                                <UserQueryCTR
                                    gameplay={product.userGameplay}
                                    endScreen={product.userEndScreen}
                                    impressions={product.userImpressions}
                                />
                            </Col>
                        </>
                    )}
                    <Col className="d-flex col-1 justify-content-end px-1">
                        {analyticsEnabled && analyticsFunnelSelectionMode ? (
                            <AnalyticsFunnelSelectionToggle
                                className="custom-funnel-button"
                                reference="product"
                                product={{
                                    ...product,
                                    selected: true,
                                    comparison: `product-${product.id}`
                                }}
                            />
                        ) : null}
                        {actions.length > 0 ? (
                            <ActionDropdown
                                id={`product-${product.id}`}
                                variant="primary"
                                actions={actions}
                            />
                        ) : null}
                    </Col>
                </Row>
            </Card.Header>
            <Collapse in={showChildren && loaded}>
                <div>
                    <Card.Body className="custom-card-body mt-1">
                        <Projects
                            productId={product.id}
                            show={showChildren}
                            updateLoaded={updateLoaded}
                        />
                    </Card.Body>
                </div>
            </Collapse>
            {showRemoveAlert ? (
                <AlertModal
                    header={<h5>Remove Product from Favorites</h5>}
                    component={
                        favoriteProjects.length === 1
                            ? "This will also remove 1 favorited project from your favorites list."
                            : `This will also remove ${favoriteProjects.length} favorited projects from your favorites list.`
                    }
                    visible={() => updateShowRemoveAlert(false)}
                    footer={
                        <Button
                            variant="danger"
                            className="m-2"
                            onClick={() => {
                                handleFavoriteUpdate();
                                updateShowRemoveAlert(false);
                            }}
                        >
                            Remove from Favorites
                        </Button>
                    }
                />
            ) : null}
        </Card>
    );
};

export const ProductCardItem = React.memo(ProductCardItemImplementation);
